// src/pages/OrderDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import orderService from '../services/orderService';
import OrderDetails from '../components/order/OrderDetails';
import Loading from '../components/common/Loading';
import Button from '../components/common/Button';

/**
 * Order Detail Page Component
 * 
 * Features:
 * - Display complete order information
 * - Show all order items (with custom names)
 * - Delivery address
 * - Payment details
 * - Order tracking
 */
const OrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    fetchOrderDetails();
  }, [id, isAuthenticated, navigate]);
  
  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const data = await orderService.getOrderById(id);
      setOrder(data);
    } catch (error) {
      console.error('Error fetching order details:', error);
      alert('Order not found');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return <Loading fullScreen message="Loading order details..." />;
  }
  
  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Order not found</h2>
            <Link to="/orders">
              <Button className="mt-4">Back to Orders</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <div className="mb-6">
          <Link to="/orders">
            <Button variant="outline">
              ← Back to Orders
            </Button>
          </Link>
        </div>
        
        {/* Order Details */}
        <OrderDetails order={order} />
      </div>
    </div>
  );
};

export default OrderDetailPage;